import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { closedDaysLabel, hoursLabel } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mainNav } from '../../data/navigation';
import { legalDocs } from '../../data/legal';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { isOpen } from '../../core/rules/schedule';
import { ease } from '../../core/format/motion';
import { instagramHandle, lowerFirst } from '../../core/format/text';
import { NewsletterSignup } from './NewsletterSignup';
const colHeading = 'text-[13px] text-shell';
const link = 'underline-offset-4 transition-[text-decoration-color] duration-150 ease-soft hover:underline';

/**
 * De footer, op klei: het donkere slot van elke pagina. Nieuwsbrief groot bovenaan, daaronder
 * vier rustige kolommen (wie, bezoek, tijden, ontdek) en als afsluiting de naam van de salon
 * over de volle breedte, toonsgewijs in het klei: een signatuur, geen extra informatie.
 *
 * Contrast: alle tekst shell op klei (5,6:1). De focusring is hier shell in plaats van klei.
 */
export function Footer() {
  const reduce = useReducedMotion();
  // Gegevens en tijden uit de actuele instellingen (Instellingen › Salon & contact en › Salondagen).
  const {
    salon,
    openingHours
  } = useSite();
  // De woorden uit Paginateksten › Kop en voet.
  const copy = usePages().chrome.footer;
  return <footer className="overflow-hidden bg-clay text-shell [&_*:focus-visible]:outline-shell" data-id="element-7832" data-mp="208rq4kylttid">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7833" data-mp="1m9jrpua9mpf8">
        <NewsletterSignup data-id="element-7834" data-mp="1r83dnuov017l" />

        <div className="grid grid-cols-2 gap-x-6 gap-y-12 border-t border-shell/25 py-14 lg:grid-cols-12 lg:gap-x-10 lg:py-16" data-id="element-7835" data-mp="1r7bhoirco1gs">
          <div className="col-span-2 lg:col-span-4" data-id="element-7836" data-mp="awze0epc3cby">
            <p className="type-title" data-id="element-7837" data-mp="fuit1gtuvt4y">{salon.name}</p>
            {copy.intro && <p className="mt-3 max-w-sm text-[15px] leading-relaxed" data-id="element-7838" data-mp="3rxisb8s4h845">{fill(copy.intro, {
              city: salon.city
            })}</p>}
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[15px]" data-id="element-7839" data-mp="3nv5mylhafc7w">
              {salon.instagram && <li data-id="element-7840" data-mp="3j3e4ky8accsu">
                  <a href={salon.instagram} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7841" data-mp="3j2uprqdxm4j1">
                    Instagram <span className="text-shell" data-id="element-7842" data-mp="396rladvlxtsx">@{instagramHandle(salon.instagram, salon.name)}</span>
                    <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7843" data-mp="34755pxsb41gy" />
                    <span className="sr-only" data-id="element-7844" data-mp="306urjxud24r0"> {shopLabels.newTab}</span>
                  </a>
                </li>}
              <li data-id="element-7845" data-mp="2v8kvll0tb04c">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center gap-1.5 ${link}`} data-id="element-7846" data-mp="2v930f61jimnv">
                  WhatsApp
                  <ArrowUpRightIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7847" data-mp="tfwnoyd83oi8" />
                  <span className="sr-only" data-id="element-7848" data-mp="10yczag4r081a"> {shopLabels.newTab}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7849" data-mp="1aophi1p3bp49">
            <h2 className={colHeading} data-id="element-7850" data-mp="1aphdveqihqvl">
              <Link to="/contact" className={link} data-id="element-7851" data-mp="1fniwxznxtq72">
                Bezoek & contact
              </Link>
            </h2>
            <address className="mt-4 space-y-3 text-[15px] not-italic leading-relaxed" data-id="element-7852" data-mp="i0tl6y3fnz80">
              <p data-id="element-7853" data-mp="myli66ixqbj2">
                {salon.street}
                <br data-id="element-7854" data-mp="n17m7w6ma8wj" />
                {salon.postalCode} {salon.locality}
                <br data-id="element-7855" data-mp="rxevnyy822j8" />
                <a href={salon.routeUrl} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center underline ${link}`} data-id="element-7856" data-mp="rwwseid56o1t">
                  Plan je route<span className="sr-only" data-id="element-7857" data-mp="23e4wt3nsg45z"> {shopLabels.newTab}</span>
                </a>
              </p>
              <p data-id="element-7858" data-mp="27dx83o4kgof1">
                <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={link} data-id="element-7859" data-mp="27ddnfznj7lxb">
                  <span className="sr-only" data-id="element-7860" data-mp="25yb9bzqgx2y5">WhatsApp: </span>
                  {salon.phone}
                  <span className="sr-only" data-id="element-7861" data-mp="20zzxxkkiykdt"> {shopLabels.newTab}</span>
                </a>
                <br data-id="element-7862" data-mp="2fx0eqt16guxi" />
                <a href={`mailto:${salon.email}`} className={`break-all ${link}`} data-id="element-7863" data-mp="2fxjwo9qtvjbj">
                  <span className="sr-only" data-id="element-7864" data-mp="2aw30grjagl1a">E-mail: </span>
                  {salon.email}
                </a>
              </p>
            </address>
            <p className="mt-4 text-[13px] leading-relaxed" data-id="element-7865" data-mp="2jxkdu25ba8ma">{policyCopy.response}</p>
            {salon.englishNote && <p className="mt-1 text-[13px] leading-relaxed" data-id="element-7866" data-mp="2ym73awonkgr8">{salon.englishNote}</p>}
          </div>

          <div className="col-span-2 sm:col-span-1 lg:col-span-3" data-id="element-7867" data-mp="2tnnfeqqi0uw8">
            <h2 className={colHeading} data-id="element-7868" data-mp="2tpgoecol3r7e">{fill(copy.hoursHeading)}</h2>
            <dl className="mt-4 text-[15px]" data-id="element-7869" data-mp="37ny7ty7gihji">
              {openingHours.filter(isOpen).map(h => <div key={h.day} className="flex justify-between gap-4 border-b border-shell/20 py-2" data-id="element-7870" data-mp="33muq165umbfn">
                  <dt data-id="element-7871" data-mp="3made83lda7fz">{h.day}</dt>
                  <dd className="tabular-nums" data-id="element-7872" data-mp="3m810y3qk0l99">{hoursLabel(h)}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-[13px]" data-id="element-7873" data-mp="3d9fh859cj06w">{closedDaysLabel(openingHours)}</p>
            <p className="mt-1 text-[13px]" data-id="element-7874" data-mp="3dbi4win10h8g">De webshop is altijd open: {lowerFirst(policyCopy.delivery)}.</p>
          </div>

          <nav aria-label="Footernavigatie" className="col-span-2 lg:col-span-2" data-id="element-7875" data-mp="33iud62awl3gl">
            <h2 className={colHeading} data-id="element-7876" data-mp="38a2m6x8bmmt8">Ontdek</h2>
            <ul className="mt-2 grid grid-cols-2 text-[15px] lg:mt-3 lg:grid-cols-1" data-id="element-7877" data-mp="38aufg1eg48gc">
              {mainNav.map(l => <li key={l.to} data-id="element-7878" data-mp="2ugl3tfrdfg4s">
                  <Link to={l.to} className={`inline-flex min-h-[44px] items-center lg:min-h-[36px] ${link}`} data-id="element-7879" data-mp="2yjzseutdl4tu">
                    {l.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>
      </div>

      {/* De signatuur: de naam over de volle breedte, toonsgewijs. Decoratie; de naam staat hierboven al als tekst. */}
      <div aria-hidden="true" className="select-none overflow-hidden" data-id="element-7880" data-mp="v579pymsialc">
        <motion.p initial={reduce ? false : {
        y: '35%',
        opacity: 0
      }} whileInView={{
        y: '0%',
        opacity: 1
      }} viewport={{
        once: true,
        margin: '0px 0px -10% 0px'
      }} transition={{
        duration: 0.3,
        ease
      }} className="ornament-wordmark text-center text-shell/[0.14]" data-id="element-7881" data-mp="qbcvvbtv8pu6">
          {salon.name}
        </motion.p>
      </div>

      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7882" data-mp="1a1opd220ibdp">
        <div className="flex flex-col gap-3 border-t border-shell/25 py-6 text-[13px] sm:flex-row sm:items-center sm:justify-between" data-id="element-7883" data-mp="151k3sxm8yqgm">
          <p data-id="element-7884" data-mp="101ea7bwf2tvw">
            © {new Date().getFullYear()} {salon.name} · KvK {salon.kvk}
            {salon.vatId && <> · Btw-id {salon.vatId}</>}
          </p>
          {legalDocs.length > 0 && <nav aria-label="Juridisch" data-id="element-7885" data-mp="3krn4myzasrzg">
              <ul className="flex flex-wrap gap-x-5 sm:gap-y-2" data-id="element-7886" data-mp="3kurjcqae364d">
                {legalDocs.map(d => <li key={d.path} data-id="element-7887" data-mp="3amx0qnkc3dsl">
                    <Link to={d.path} className={`inline-flex min-h-[44px] items-center sm:min-h-0 ${link}`} data-id="element-7888" data-mp="3fn1iegwzaeke">
                      {d.title}
                    </Link>
                  </li>)}
              </ul>
            </nav>}
        </div>
      </div>
    </footer>;
}